import React from "react";
import { Link } from "react-router-dom";
import { getPlatform } from "../../utils/platforms";
import { formatOutlier, buildHistogramBars } from "../../utils/numbers";
import VideoPreview from "../common/VideoPreview";
import HeroIcon from "../common/HeroIcon";

const PATTERN_TYPE_LABELS = {
  type: "Content Type",
  format: "Creative Device",
  structure: "Content Structure",
  main_topic: "Content Topic",
  content_angle: "Content Angle",
  categories: "Content Pillar",
  tone: "Content Tone",
  audience: "Audience Segment",
  language: "Audience Language",
  hook_type: "Hook Type",
  hook_phrase: "Hook Phrase",
  hook_visual: "Visual Hook",
  cta_type: "CTA Action",
  cta_style: "CTA Line",
  hashtags: "Hashtags",
  channel_tags: "Mentions",
  text_hook: "Caption Hook",
  song: "Song",
  artist: "Artist",
};

const formatNumber = (num) => {
  if (!num) return "0";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};


const OutlierVsCreator = ({ outlierRatio }) => {
  const outlier = formatOutlier(outlierRatio);
  if (!outlier) return <span className="text-theme-text opacity-40">—</span>;
  return (
    <span className={outlierRatio === 1 ? "text-theme-text opacity-60 font-medium" : outlier.above ? "text-green-500 font-medium" : "text-red-500 font-medium"}>
      {outlier.text}
    </span>
  );
};

/** Simple non-interactive histogram: creator distribution with current value's bin highlighted. Max 10 bars. Log scale heights. */
const StatHistogram = ({ values, currentValue }) => {
  const bars = buildHistogramBars(values, currentValue, 10);
  if (!bars.length) return null;
  const logHeights = bars.map((b) => Math.log(b.count + 1));
  const maxLog = Math.max(...logHeights, 1);
  const minHeightPct = 8;
  return (
    <div className="flex items-end gap-px h-8" aria-hidden>
      {bars.map((bar, i) => {
        const heightPct = Math.max((logHeights[i] / maxLog) * 100, minHeightPct);
        const isHighlighted = bar.isHighlighted;
        const isEmpty = bar.count === 0;
        const barClass = isHighlighted
          ? "bg-theme-primary opacity-70"
          : isEmpty
            ? "bg-theme-shadow opacity-50"
            : "bg-theme-shadow";
        return (
          <div
            key={i}
            className={`flex-1 min-w-0 rounded-t-sm transition-opacity ${barClass}`}
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </div>
  );
};

const PatternTag = ({ p, audienceHash }) => {
  const typeLabel = PATTERN_TYPE_LABELS[p.type] || p.type;
  const viewsRatio = p.pattern?.creator_comparison?.views;
  const viewsOutlier = formatOutlier(viewsRatio);
  const viewsColorClass = !viewsOutlier
    ? ""
    : viewsRatio === 1
      ? "text-theme-text opacity-40"
      : viewsOutlier.above
        ? "text-green-500"
        : "text-red-500";
  const tag = (
    <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-theme-secondary bg-theme-shadow text-xs w-full">
      {p.icon && <HeroIcon name={p.icon} className="w-3.5 h-3.5 flex-shrink-0 text-theme-text opacity-40" />}
      <span className="text-theme-text opacity-50 font-medium flex-shrink-0">{typeLabel}:</span>
      <span className="text-theme-text font-medium flex-1 truncate">{p.name}</span>
      {viewsOutlier && (
        <span className={`font-semibold flex-shrink-0 ${viewsColorClass}`}>
          {viewsOutlier.text} views
        </span>
      )}
    </span>
  );
  if (audienceHash && p.type && p.key) {
    return (
      <Link className="block w-full hover:opacity-80 transition-opacity" to={`/audience/${audienceHash}/patterns/${encodeURIComponent(p.type)}/${encodeURIComponent(p.key)}/`}>
        {tag}
      </Link>
    );
  }
  return tag;
};

/**
 * Renders the full video detail content (stats, analysis, fit reasoning).
 * No modal wrapper — embed this anywhere, including in the VideoDetailModal.
 * When audienceHash is provided and item.creator has hash, the creator name links to the creator detail page.
 */
const VideoDetail = ({ item, audienceHash }) => {
  if (!item) return null;

  const video = item.video;
  const creator = item.creator;
  const platform = getPlatform(video?.platform);
  const platformLabel = video?.platform
    ? video.platform.charAt(0).toUpperCase() + video.platform.slice(1)
    : "Platform";
  const engagementDisplay = video?.engagement != null ? `${Number(video.engagement).toFixed(2)}%` : "—";

  const stats = [
    { label: "Views", value: video?.count_views ?? 0, display: formatNumber(video?.count_views ?? 0), outlier: item.creator_views_outlier, values: item.creator_views_all, currentValue: video?.count_views },
    { label: "Likes", value: video?.count_likes ?? 0, display: formatNumber(video?.count_likes ?? 0), outlier: item.creator_likes_outlier, values: item.creator_likes_all, currentValue: video?.count_likes },
    { label: "Comments", value: video?.count_comments ?? 0, display: formatNumber(video?.count_comments ?? 0), outlier: item.creator_comments_outlier, values: item.creator_comments_all, currentValue: video?.count_comments },
    { label: "Engagement", value: engagementDisplay, display: engagementDisplay, outlier: item.creator_engagement_outlier, values: item.creator_engagement_all, currentValue: video?.engagement != null ? Number(video.engagement) : null },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            {platform?.icon && (
              <div className="w-6 h-6 flex-shrink-0">
                {React.cloneElement(platform.icon, { className: "w-6 h-6" })}
              </div>
            )}
            <h3 className="text-xl font-semibold text-theme-text truncate">
              {video?.title || video?.platform_id || "Video"}
            </h3>
          </div>
          {creator && (
            <p className="text-sm text-theme-text opacity-70">
              by{" "}
              {audienceHash && creator.hash ? (
                <Link
                  to={`/audience/${audienceHash}/creators/${creator.hash}/`}
                  className="hover:underline"
                >
                  {creator.name || creator.platform_id}
                </Link>
              ) : (
                (creator.name || creator.platform_id)
              )}
              {creator.follower_count != null && creator.follower_count > 0 && (
                <>
                  {" · "}
                  {formatNumber(creator.follower_count)} followers
                </>
              )}
            </p>
          )}
        </div>
        {video?.platform_url && (
          <a
            href={video.platform_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 px-3 py-1.5 text-sm text-white rounded-md hover:opacity-90 inline-flex items-center gap-1.5"
            style={{ backgroundColor: platform?.color ?? "#6b7280" }}
          >
            {platform?.icon && React.cloneElement(platform.icon, { className: "w-4 h-4" })}
            View on {platformLabel}
          </a>
        )}
      </div>

      {/* Performance Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-theme-secondary rounded-lg border border-theme-secondary p-4 flex flex-col">
            <div className="text-xs text-theme-text opacity-70 uppercase tracking-wider mb-1">{stat.label}</div>
            <div className="flex items-end gap-3">
              <div className="text-xl font-bold text-theme-text flex-1 min-w-0">{stat.display}</div>
              <div className="w-1/2 flex-shrink-0">
                <StatHistogram values={stat.values} currentValue={stat.currentValue} />
              </div>
            </div>
            <div className="text-sm mt-1">
              <OutlierVsCreator outlierRatio={stat.outlier} /> <span className="text-theme-text opacity-50 text-xs">vs creator median</span>
            </div>
          </div>
        ))}
      </div>

      {/* Content Analysis: left = video embed, right = summary + patterns */}
      <div>
        <h4 className="text-sm font-semibold text-theme-text opacity-90 uppercase tracking-wider mb-3">
          Content Analysis
        </h4>
        <div className="grid grid-cols-3 gap-4 items-start">
          <div>
            <VideoPreview video={video} />
          </div>
          <div className="col-span-2 bg-theme-secondary rounded-lg border border-theme-secondary p-4 flex flex-col gap-3">
            {item.content ? (
              <div className="flex flex-col gap-2">
                {[
                  { label: "Summary", key: "summary" },
                  { label: "Hook", key: "hook" },
                  { label: "Format", key: "format" },
                ].map((field, idx) => (
                  <div key={field.key} className={idx === 0 ? "" : "pt-2 border-t border-theme-secondary"}>
                    <div className="text-[10px] uppercase tracking-wider text-theme-text opacity-60">{field.label}</div>
                    <div className="text-sm text-theme-text whitespace-pre-wrap break-words mt-0.5">
                      {item.content[field.key] ? (
                        String(item.content[field.key]).trim()
                      ) : (
                        <span className="opacity-50">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-theme-text opacity-40">No content available yet.</p>
            )}
            {item.patterns && item.patterns.length > 0 && (
              <div className="flex flex-col gap-1.5 pt-1 border-t border-theme-secondary">
                {item.patterns.map((p) => (
                  <PatternTag key={`${p.type}-${p.key}`} p={p} audienceHash={audienceHash} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fit Reasoning */}
      {item.fit_reasoning && (
        <div>
          <h4 className="text-sm font-semibold text-theme-text opacity-90 uppercase tracking-wider mb-2">
            Why This Video Fits
          </h4>
          <p className="text-sm text-theme-text opacity-80 bg-theme-secondary rounded-lg border border-theme-secondary p-4">
            {item.fit_reasoning}
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoDetail;
